'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Search,
  Users,
  Sparkles,
  ShieldAlert,
  Image as ImageIcon,
  Scale,
  Radar,
  Filter,
  AlertTriangle,
  Star,
  Check,
} from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */

function RadarSweepBg({ tint }) {
  /* Radar grid scanning the market — topic blips across rings */
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.7 }} aria-hidden="true">
      <defs>
        <radialGradient id="rs-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.15" />
          <stop offset="60%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rs-sweep" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.30" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle cx="200" cy="200" r="180" fill="url(#rs-glow)" />
      {[60, 110, 160].map((r, i) => (
        <circle key={r} cx="200" cy="200" r={r} fill="none" stroke={tint.accent} strokeOpacity={0.30 - i * 0.06} strokeWidth="1" />
      ))}
      <line x1="200" y1="40" x2="200" y2="360" stroke={tint.accent} strokeOpacity="0.18" strokeDasharray="4 6" />
      <line x1="40" y1="200" x2="360" y2="200" stroke={tint.accent} strokeOpacity="0.18" strokeDasharray="4 6" />
      <path d="M 200 200 L 360 200 A 160 160 0 0 0 270 65 Z" fill="url(#rs-sweep)" />
      {[
        { x: 250, y: 130, r: 6 },
        { x: 145, y: 165, r: 4 },
        { x: 300, y: 235, r: 8 },
        { x: 170, y: 275, r: 5 },
        { x: 230, y: 95,  r: 4 },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={p.r + 4} fill={tint.accent} fillOpacity="0.18" />
          <circle cx={p.x} cy={p.y} r={p.r} fill={tint.accent} />
        </g>
      ))}
      <circle cx="200" cy="200" r="4" fill={tint.accent} />
    </svg>
  );
}

function FunnelStagesBg({ tint }) {
  /* Customer journey funnel — Awareness → Consideration → Conversion → Loyalty */
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.72 }} aria-hidden="true">
      <defs>
        <radialGradient id="fs-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#fs-glow)" />
      {[
        { y: 70,  w: 280, h: 42, op: 0.20 },
        { y: 130, w: 230, h: 42, op: 0.32 },
        { y: 190, w: 180, h: 42, op: 0.48 },
        { y: 250, w: 130, h: 42, op: 0.70 },
      ].map((b, i) => (
        <g key={i}>
          <rect x={(400 - b.w) / 2} y={b.y} width={b.w} height={b.h} rx="10" fill={tint.accent} fillOpacity={b.op} />
          <line x1={(400 - b.w) / 2 + 20} y1={b.y + 16} x2={(400 - b.w) / 2 + b.w - 60} y2={b.y + 16} stroke="#fff" strokeOpacity="0.7" strokeWidth="2" strokeLinecap="round" />
          <line x1={(400 - b.w) / 2 + 20} y1={b.y + 28} x2={(400 - b.w) / 2 + b.w - 100} y2={b.y + 28} stroke="#fff" strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />
        </g>
      ))}
      {[112, 172, 232].map((y, i) => (
        <g key={i}>
          <line x1="200" y1={y} x2="200" y2={y + 16} stroke={tint.accent} strokeOpacity="0.5" strokeWidth="1.5" />
          <path d={`M 195 ${y + 11} L 200 ${y + 17} L 205 ${y + 11}`} fill="none" stroke={tint.accent} strokeOpacity="0.5" strokeWidth="1.5" strokeLinejoin="round" />
        </g>
      ))}
      <path d="M 200 320 C 195 312, 184 312, 184 322 C 184 332, 200 342, 200 342 C 200 342, 216 332, 216 322 C 216 312, 205 312, 200 320 Z" fill={tint.accent} fillOpacity="0.65" />
    </svg>
  );
}

function RiskRadarBg({ tint }) {
  /* Risk/alert radar — warning triangles + safe ticks + alert pulse */
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.72 }} aria-hidden="true">
      <defs>
        <radialGradient id="rr-glow" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.14" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#rr-glow)" />
      <path d="M 200 60 L 320 100 L 320 200 Q 320 290, 200 340 Q 80 290, 80 200 L 80 100 Z" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.5" strokeDasharray="6 4" />
      <path d="M 200 90 L 290 122 L 290 200 Q 290 274, 200 314 Q 110 274, 110 200 L 110 122 Z" fill={tint.accent} fillOpacity="0.08" stroke={tint.accent} strokeOpacity="0.20" />
      {[
        { x: 145, y: 135 },
        { x: 270, y: 160 },
        { x: 230, y: 250 },
      ].map((p, i) => (
        <g key={i}>
          <path d={`M ${p.x} ${p.y - 12} L ${p.x + 14} ${p.y + 12} L ${p.x - 14} ${p.y + 12} Z`} fill={tint.accent} fillOpacity="0.85" />
          <circle cx={p.x} cy={p.y + 6} r="1.5" fill="#fff" />
          <line x1={p.x} y1={p.y - 4} x2={p.x} y2={p.y + 2} stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx={p.x} cy={p.y} r="22" fill="none" stroke={tint.accent} strokeOpacity={0.25 - i * 0.05} strokeWidth="1" />
        </g>
      ))}
      {[
        { x: 170, y: 210 },
        { x: 250, y: 215 },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="11" fill="#fff" stroke={tint.accent} strokeOpacity="0.45" />
          <path d={`M ${p.x - 4} ${p.y} L ${p.x - 1} ${p.y + 3} L ${p.x + 5} ${p.y - 3}`} fill="none" stroke={tint.accent} strokeOpacity="0.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ))}
    </svg>
  );
}

function ReviewCardsBg({ tint }) {
  /* Customer experience: review cards with stars + comparison bars */
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.75 }} aria-hidden="true">
      <defs>
        <radialGradient id="rc-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#rc-glow)" />
      {[
        { y: 60,  fill: 0.85, w: 240, stars: 5 },
        { y: 145, fill: 0.55, w: 220, stars: 4 },
        { y: 230, fill: 0.30, w: 200, stars: 5 },
      ].map((c, i) => (
        <g key={i}>
          <rect x={(400 - c.w) / 2} y={c.y} width={c.w} height="62" rx="10" fill="#fff" stroke={tint.accent} strokeOpacity={0.35 + (i === 0 ? 0.15 : 0)} />
          <circle cx={(400 - c.w) / 2 + 20} cy={c.y + 22} r="10" fill={tint.accent} fillOpacity="0.30" />
          <line x1={(400 - c.w) / 2 + 40} y1={c.y + 18} x2={(400 - c.w) / 2 + 130} y2={c.y + 18} stroke={tint.accent} strokeOpacity="0.55" strokeWidth="2.5" strokeLinecap="round" />
          {Array.from({ length: c.stars }).map((_, s) => {
            const sx = (400 - c.w) / 2 + 40 + s * 11;
            const sy = c.y + 30;
            return (
              <path key={s} d={`M ${sx} ${sy - 4} L ${sx + 1.2} ${sy - 1.2} L ${sx + 4} ${sy - 1} L ${sx + 1.8} ${sy + 1.3} L ${sx + 2.3} ${sy + 4} L ${sx} ${sy + 2.4} L ${sx - 2.3} ${sy + 4} L ${sx - 1.8} ${sy + 1.3} L ${sx - 4} ${sy - 1} L ${sx - 1.2} ${sy - 1.2} Z`} fill={tint.accent} fillOpacity={c.fill} />
            );
          })}
          <line x1={(400 - c.w) / 2 + 20} y1={c.y + 46} x2={(400 - c.w) / 2 + c.w - 30} y2={c.y + 46} stroke={tint.accent} strokeOpacity="0.20" strokeWidth="2" strokeLinecap="round" />
          <line x1={(400 - c.w) / 2 + 20} y1={c.y + 54} x2={(400 - c.w) / 2 + c.w - 70} y2={c.y + 54} stroke={tint.accent} strokeOpacity="0.20" strokeWidth="2" strokeLinecap="round" />
        </g>
      ))}
      <g>
        <line x1="100" y1="335" x2="300" y2="335" stroke={tint.accent} strokeOpacity="0.20" />
        <rect x="100" y="318" width="120" height="6" rx="3" fill={tint.accent} fillOpacity="0.75" />
        <rect x="100" y="328" width="80"  height="6" rx="3" fill={tint.accent} fillOpacity="0.35" />
      </g>
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Market Research',
  tagline: 'Know What the Market Feels. Act Before the Market Moves.',
  body: [
    'Uncover what the world feels, needs, and expects — before the market even moves. Locobuzz’s AI-driven Market Research platform analyzes billions of omnichannel data points, and our generative AI uncovers predictive consumer insights, audience behaviors, and emerging industry trends.',
    'Equip your enterprise leaders with the empirical data required to validate strategic product and marketing decisions.',
  ],
};

/* DRAFT — review */
/* Impact band — the dark "scale proof" moment under the hero. */
const IMPACT = [
  { value: 'Billions', label: 'Data points analyzed across channels' },
  { value: '95%+', label: 'Accuracy in predictive trend detection' },
  { value: '4x', label: 'Faster consumer insight cycles' },
  { value: '30+', label: 'Markets and languages covered' },
];
/* end DRAFT */

const STACK_POINTS = [
  { Icon: Users,       title: 'Personas at Scale',    text: 'Craft high-converting content & buyer personas using demographic & behavioral data.', tint: 'blue' },
  { Icon: Sparkles,    title: 'Real-Time Messaging',   text: 'Align messaging in real time with trending topics and emotional sentiment cues.',    tint: 'violet' },
  { Icon: ShieldAlert, title: 'Crisis Early-Warning',  text: 'Detect early signs of brand impersonation, instability, or IP misuse before impact.', tint: 'sky' },
  { Icon: Filter,      title: 'Journey Mapping',        text: 'Map complete customer journeys across awareness, consideration, and loyalty stages.', tint: 'blue' },
  { Icon: ImageIcon,   title: 'Visual UGC Decoding',    text: 'Decode visual UGC to understand brand perception, emotional association, and risks.', tint: 'violet' },
  { Icon: Scale,       title: 'Competitive Benchmarks', text: 'Benchmark shifting consumer needs and product feedback to stay positioned.',         tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Market Discovery',
    title: 'Know What Moves Your Market',
    lead: 'Track brand, product, and industry conversations as they happen — on social, news, forums, and beyond.',
    bullets: [
      'Uncover emerging themes and sentiment trends across public conversations',
      'Tap into demographic and psychographic profiles to build rich customer personas',
      'Identify unmet needs and whitespace to spot brand opportunities or pain points with AI',
    ],
    cta: { label: 'Start Listening', href: '#book-demo' },
    visual: { Icon: Radar, title: 'Market Radar', subtitle: 'Themes · Personas · Whitespace', Bg: RadarSweepBg, tint: 'blue' },
  },
  {
    eyebrow: 'Customer Journey',
    title: 'Convert the First Click to Fierce Loyalty',
    lead: 'Go beyond sentiment to understand who’s talking, how they feel, and what they move.',
    bullets: [
      'Map conversations to customer journey stages with custom tags',
      'Spot friction points, drop-offs, or delight moments using sentiment overlays',
      'Compare cohort behaviors across time, campaigns, or regions',
    ],
    cta: { label: 'Know More', href: '#book-demo' },
    visual: { Icon: Filter, title: 'Journey Funnel', subtitle: 'Awareness → Loyalty', Bg: FunnelStagesBg, tint: 'ice' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'Risk Intelligence',
    title: 'From Risk to Readiness in Real Time',
    lead: 'Decode how your content made audiences feel — curiosity, trust, frustration, or joy — and act before risk becomes reality.',
    bullets: [
      'Monitor political, social, and economic shifts across key markets in real time',
      'Track negative sentiment aimed at leadership or employees to prevent PR fallout',
      'Build proactive crisis response strategies based on predictive alerts and context',
    ],
    cta: { label: 'Measure Campaigns', href: '#book-demo' },
    visual: { Icon: AlertTriangle, title: 'Risk Radar', subtitle: 'Predictive Alerts & Context', Bg: RiskRadarBg, tint: 'sky' },
  },
  {
    eyebrow: 'CX Intelligence',
    title: 'Elevate Every Customer Experience',
    lead: 'Compare your digital footprint against competitors at speed and scale.',
    bullets: [
      'Monitor customer reviews, queries, and mentions across platforms in real time',
      'Never miss a moment with alerts for priority topics or negative sentiment spikes',
      'Centralize brand intelligence and share insights internally for aligned action',
      'Benchmark experience against competitors to drive CX differentiation',
    ],
    visual: { Icon: Star, title: 'CX Benchmarks', subtitle: 'Reviews · Ratings · Comparisons', Bg: ReviewCardsBg, tint: 'blue' },
    reverse: true,
  },
];

/* DRAFT — review */
const FAQS = [
  { q: 'What is AI-driven market research?', a: 'AI-driven market research uses machine learning and natural language processing to analyze billions of omnichannel data points — social conversations, reviews, forums, and news — delivering predictive consumer insights and trend detection far faster than traditional survey-based methods.' },
  { q: 'How does Locobuzz identify emerging consumer trends?', a: 'Locobuzz continuously scans public conversations across social, news, and forums in real time. Our ContextualPulse™ engine detects rising themes, sentiment shifts, and behavioral cues before they peak, so you can act on signals rather than lagging data.' },
  { q: 'Can I map insights to specific customer journey stages?', a: 'Yes. The Journey Funnel view lets you tag and map conversations to awareness, consideration, conversion, and loyalty stages — surfacing friction points and delight moments at every step using sentiment overlays and cohort comparisons.' },
  { q: 'How does Locobuzz handle risk and crisis detection?', a: 'Our Risk Intelligence layer monitors political, social, and economic shifts alongside sentiment directed at leadership or brand assets. Predictive alerts give your team context to build proactive response playbooks before a situation escalates.' },
  { q: 'How quickly can teams start getting insights?', a: 'Most teams go live quickly with guided onboarding. Book a demo and a specialist will tailor a rollout to your brand, target markets, and research use cases.' },
];
/* end DRAFT */

/* DRAFT — review */
/* Hero product panel data — market-research themed. */
const PANEL = {
  title: 'Market Pulse',
  badge: 'GenAI insights',
  kpiValue: 94210,
  kpiLabel: 'Consumer signals analyzed today',
  trend: [8, 14, 11, 19, 24, 20, 31, 28, 40, 58],
  trendDelta: '+38%',
  segments: [
    { label: 'Brand themes', pct: 45, tone: 'brand' },
    { label: 'Neutral signals', pct: 33, tone: 'neu' },
    { label: 'Positive sentiment', pct: 22, tone: 'pos' },
  ],
  feedLabel: 'Emerging insights',
  feed: [
    { text: 'Consumers in Tier-2 cities demand faster delivery windows 📦', meta: 'E-commerce · India', tag: 'Trend', tone: 'brand' },
    { text: 'Sustainability packaging now a top purchase trigger for Gen Z', meta: 'FMCG · Global', tag: 'Insight', tone: 'pos' },
    { text: 'Price sensitivity rising in mid-income cohort — value messaging needed', meta: 'Retail · South Asia', tag: 'Signal', tone: 'neu' },
    { text: 'Competitor X gaining mindshare in premium segment — monitor closely', meta: 'Competitive · APAC', tag: 'Alert', tone: 'neu' },
    { text: 'Brand trust scores up 12% following new campaign launch 🎯', meta: 'Brand health · All regions', tag: 'Pulse', tone: 'pos' },
  ],
  chip: 'ContextualPulse™ · trend detected',
};
/* end DRAFT */

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function MarketResearchPage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="mr-hero-orbs">
          <span className="mr-orb mr-orb1" />
          <span className="mr-orb mr-orb2" />
        </div>
        <InlineStyle css={`
          .mr-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .mr-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .mr-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:mrOrb1 22s ease-in-out infinite; }
          .mr-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:mrOrb2 26s ease-in-out infinite; }
          @keyframes mrOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes mrOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .mr-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Market Research')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Market Research')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Market')} <span className="serif-accent italic">{t('Research')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      {/* DRAFT — review: values are placeholder; confirm with marketing */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE STACK ──────────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('The Intelligence Stack')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Intelligence Stack That Powers Faster,')} <span className="serif-accent italic">{t('Smarter Decisions')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 720, margin: '0 auto', lineHeight: 1.7 }}>{t('Make every strategy sharper with real-time insights from sentiment, signals, and social behaviors. Unlock decision-making that’s fast, focused, and future-ready.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('The Intelligence Engine')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Full-Stack Behind')} <span className="serif-accent italic">{t('Sharper Market Intelligence')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 720, margin: '0 auto', lineHeight: 1.7 }}>{t('Locobuzz scans millions of signals across social media, forums, reviews, and visual UGC to uncover what your audience is saying, showing, and feeling.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {/* DRAFT — review: 5 FAQ items; refine copy before rollout */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="market-research-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
